import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { ROLE_IDS, canManageUserDirectory } from '../utils/accessControl';
import '../styles/Users.css';

const ROLE_META = {
  Admin: { label: 'Admin - Tam Erişim', color: '#e74c3c' },
  'Project Manager': { label: 'Proje Müdürü', color: '#3498db' },
  'Site Supervisor': { label: 'Şantiye Şefi', color: '#2ecc71' },
  Worker: { label: 'Amele', color: '#f39c12' },
  Engineer: { label: 'Mühendis', color: '#9b59b6' },
  Accountant: { label: 'Muhasebeci', color: '#16a085' },
};

const FALLBACK_ROLES = [
  { id: 1, name: 'Admin', description: 'Full access to the system' },
  { id: 2, name: 'Project Manager', description: 'Manage projects, tasks, and approvals' },
  { id: 3, name: 'Site Supervisor', description: 'Manage field operations and reports' },
  { id: 4, name: 'Worker', description: 'Enter puantaj and complete assigned tasks' },
  { id: 5, name: 'Engineer', description: 'Professional engineer role' },
  { id: 6, name: 'Accountant', description: 'Manage monthly accounting and expense tracking' },
];

const Users = () => {
  const { user: currentUser, permissions } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState('all');
  const [expandedGroupMap, setExpandedGroupMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    phone_number: '',
    temporary_password: '',
    role_id: '',
    custom_job_title: '',
  });
  const [editFormData, setEditFormData] = useState({
    username: '',
    email: '',
    phone_number: '',
    role_id: '',
    custom_job_title: '',
    is_active: true,
  });

  const isSuperAdmin = Number(currentUser?.id) === ROLE_IDS.ADMIN;
  const isCurrentAdmin = Number(currentUser?.role_id) === ROLE_IDS.ADMIN;
  const canManageUsers = canManageUserDirectory(currentUser, permissions);

  const roleById = useMemo(() => {
    const map = new Map();
    roles.forEach((role) => map.set(Number(role.id), role));
    return map;
  }, [roles]);

  const getRoleBadge = useCallback(
    (roleId, jobTitle) => {
      const role = roleById.get(Number(roleId));
      const roleName = role?.name || 'Bilinmiyor';

      if (roleName === 'Worker' && String(jobTitle || '').trim()) {
        return {
          label: String(jobTitle).trim(),
          color: '#f39c12',
        };
      }

      const meta = ROLE_META[roleName];
      return {
        label: meta?.label || roleName,
        color: meta?.color || '#95a5a6',
      };
    },
    [roleById]
  );

  const groupedUsers = useMemo(() => {
    const grouped = new Map();
    users.forEach((entry) => {
      const roleBadge = getRoleBadge(entry.role_id, entry.job_title);
      const key = roleBadge.label || 'Diğer';
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          color: roleBadge.color,
          users: [],
        });
      }
      grouped.get(key).users.push(entry);
    });

    return Array.from(grouped.values()).sort((a, b) => a.key.localeCompare(b.key, 'tr-TR'));
  }, [users, getRoleBadge]);

  const visibleGroups = useMemo(() => {
    if (selectedGroupFilter === 'all') return groupedUsers;
    return groupedUsers.filter((group) => group.key === selectedGroupFilter);
  }, [groupedUsers, selectedGroupFilter]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/users`);
      setUsers(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch {
      setError('Kullanıcılar yüklenirken hata oluştu.');
    }
  }, []);

  const fetchRoles = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/users/roles`);
      setRoles(Array.isArray(response.data) ? response.data : []);
    } catch {
      setRoles(FALLBACK_ROLES);
      setError((prev) => prev || 'Roller servisi geçici olarak kullanılamıyor, varsayılan roller gösteriliyor.');
    }
  }, []);

  useEffect(() => {
    if (!canManageUsers) {
      setLoading(false);
      return undefined;
    }

    const bootstrap = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchRoles()]);
      setLoading(false);
    };

    bootstrap();
    return undefined;
  }, [canManageUsers, fetchRoles, fetchUsers]);

  const toggleGroup = (groupKey) => {
    setExpandedGroupMap((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      phone_number: '',
      temporary_password: '',
      role_id: '',
      custom_job_title: '',
    });
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditInputChange = (event) => {
    const { name, value } = event.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();

    if (!formData.username || !formData.role_id) {
      setError('Lütfen zorunlu alanları doldurun (Kullanıcı Adı ve Rol).');
      return;
    }

    const selectedRole = roleById.get(Number(formData.role_id));
    if (!selectedRole) {
      setError('Geçerli bir rol seçin.');
      return;
    }

    const resolvedJobTitle = formData.custom_job_title.trim() || selectedRole.name;

    try {
      const response = await apiClient.post(`${API_BASE_URL}/users`, {
        username: formData.username.trim(),
        email: formData.email.trim() || undefined,
        phone_number: formData.phone_number.trim(),
        password: formData.temporary_password.trim() || undefined,
        role_id: Number(formData.role_id),
        job_title: resolvedJobTitle,
      });

      setUsers((prev) => [response.data, ...prev]);
      setShowForm(false);
      resetForm();
      setError(null);

      if (response.data?.generated_email) {
        window.alert(`E-posta girilmediği için otomatik adres oluşturuldu: ${response.data.generated_email}`);
      }
      if (response.data?.temporary_password) {
        window.alert(`Geçici şifre otomatik oluşturuldu: ${response.data.temporary_password}`);
      }
    } catch (requestError) {
      setError(`Kullanıcı oluşturulurken hata oluştu: ${requestError.response?.data?.message || requestError.message}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) return;

    try {
      await apiClient.delete(`${API_BASE_URL}/users/${userId}`);
      setUsers((prev) => prev.filter((entry) => entry.id !== userId));
      setError(null);
    } catch {
      setError('Kullanıcı silinirken hata oluştu.');
    }
  };

  const canEditUser = (user) => {
    if (!canManageUsers || !user) return false;
    if (Number(currentUser?.role_id) === ROLE_IDS.PROJECT_MANAGER && Number(user.role_id) === ROLE_IDS.ADMIN) {
      return false;
    }
    if (Number(user.id) === Number(currentUser?.id)) return true;
    if (Number(user.role_id) === ROLE_IDS.ADMIN && !isSuperAdmin) return false;
    return true;
  };

  const canDeleteUser = useCallback(
    (targetUser) => {
      if (!targetUser || !isCurrentAdmin) return false;
      if (Number(targetUser.id) === Number(currentUser?.id)) return false;
      if (Number(targetUser.role_id) === ROLE_IDS.ADMIN && !isSuperAdmin) return false;
      return true;
    },
    [currentUser?.id, isCurrentAdmin, isSuperAdmin]
  );

  const startEditingUser = (user) => {
    setEditingUserId(user.id);
    setEditFormData({
      username: user.username || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      role_id: String(user.role_id || ''),
      custom_job_title: user.job_title || '',
      is_active: user.is_active !== false,
    });
  };

  const cancelEditingUser = () => {
    setEditingUserId(null);
    setEditFormData({
      username: '',
      email: '',
      phone_number: '',
      role_id: '',
      custom_job_title: '',
      is_active: true,
    });
  };

  const handleUpdateUser = async (user) => {
    const selectedRole = roleById.get(Number(editFormData.role_id));
    if (!selectedRole) {
      setError('Geçerli bir rol seçin.');
      return;
    }

    const resolvedJobTitle = editFormData.custom_job_title.trim() || selectedRole.name;

    try {
      const response = await apiClient.put(`${API_BASE_URL}/users/${user.id}`, {
        username: editFormData.username.trim(),
        email: editFormData.email.trim(),
        phone_number: editFormData.phone_number.trim(),
        role_id: Number(editFormData.role_id),
        job_title: resolvedJobTitle,
        ...(isCurrentAdmin ? { is_active: Boolean(editFormData.is_active) } : {}),
      });

      setUsers((prev) => prev.map((entry) => (entry.id === user.id ? { ...entry, ...response.data } : entry)));
      cancelEditingUser();
      setError(null);
    } catch (requestError) {
      setError(`Kullanıcı güncellenirken hata oluştu: ${requestError.response?.data?.message || requestError.message}`);
    }
  };

  const handleSendReminder = async (targetUser) => {
    const message = window.prompt(
      `${targetUser.username} için hatırlatma metni girin:`,
      'Lütfen bugünkü kayıtları tamamlayın.'
    );
    if (!message || !message.trim()) return;

    try {
      const response = await apiClient.post(`${API_BASE_URL}/notifications/manual`, {
        user_id: Number(targetUser.id),
        title: 'Yönetim Hatırlatması',
        message: message.trim(),
        auto_channels: true,
      });
      const channels = (response.data?.delivered_channels || []).join(', ') || 'kanal yok';
      window.alert(`Hatırlatma gönderildi. Kanallar: ${channels}`);
    } catch (requestError) {
      setError(`Hatırlatma gönderilemedi: ${requestError.response?.data?.message || requestError.message}`);
    }
  };

  const handleResetPassword = async (targetUser) => {
    const newPassword = window.prompt(
      `${targetUser.username} için yeni geçici şifre girin (boş bırakırsan sistem üretir):`,
      ''
    );
    if (newPassword === null) return;

    try {
      const response = await apiClient.post(`${API_BASE_URL}/users/${targetUser.id}/reset-password`, {
        new_password: String(newPassword || '').trim() || undefined,
      });
      const temporaryPassword = response.data?.temporary_password;
      if (temporaryPassword) {
        window.alert(`Şifre sıfırlandı. Geçici şifre: ${temporaryPassword}`);
      } else {
        window.alert('Şifre sıfırlandı.');
      }
      await fetchUsers();
    } catch (requestError) {
      setError(`Şifre sıfırlama başarısız: ${requestError.response?.data?.message || requestError.message}`);
    }
  };

  if (loading) {
    return (
      <div className="users-container">
        <div className="loading">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="users-container">
      <div className="users-header">
        <h1>Yetkili Kişiler</h1>
        <button
          className="add-user-btn"
          onClick={() => {
            resetForm();
            setShowForm((prev) => !prev);
          }}
        >
          {showForm ? 'İptal' : 'Kişi Ekle'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="user-form-container">
          <form onSubmit={handleCreateUser} className="user-form">
            <div className="form-row">
              <div className="form-group">
                <label>Kullanıcı Adı *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="örn: emrecankoc"
                  required
                />
              </div>
              <div className="form-group">
                <label>E-posta (Opsiyonel)</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Boş bırakırsan sistem otomatik oluşturur"
                />
              </div>
              <div className="form-group">
                <label>Telefon Numarası</label>
                <input
                  type="text"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                  placeholder="+905xxxxxxxxx"
                />
              </div>
              <div className="form-group">
                <label>Geçici Şifre (Opsiyonel)</label>
                <input
                  type="text"
                  name="temporary_password"
                  value={formData.temporary_password}
                  onChange={handleInputChange}
                  placeholder="Boş bırakırsan sistem üretir"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Rol / Yetki *</label>
                <select name="role_id" value={formData.role_id} onChange={handleInputChange} required>
                  <option value="">-- Rol Seçin --</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {ROLE_META[role.name]?.label || role.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Özel Görev / Ünvan (Opsiyonel)</label>
                <input
                  type="text"
                  name="custom_job_title"
                  value={formData.custom_job_title}
                  onChange={handleInputChange}
                  placeholder="Boş bırakılırsa rol adı kullanılır"
                />
              </div>
            </div>

            <button type="submit" className="submit-btn" disabled={roles.length === 0}>
              Kişiyi Ekle
            </button>
          </form>
        </div>
      )}

      <div className="users-filter-bar">
        <label htmlFor="group-filter">Göreve göre filtre:</label>
        <select
          id="group-filter"
          value={selectedGroupFilter}
          onChange={(event) => setSelectedGroupFilter(event.target.value)}
        >
          <option value="all">Tüm Görev Grupları</option>
          {groupedUsers.map((group) => (
            <option key={group.key} value={group.key}>
              {group.key} ({group.users.length})
            </option>
          ))}
        </select>
      </div>

      {visibleGroups.length === 0 ? (
        <div className="empty-state">
          <p>Filtreye uygun kullanıcı bulunamadı.</p>
          <p>Farklı bir görev grubu seçin.</p>
        </div>
      ) : (
        visibleGroups.map((group) => (
          <section key={group.key} className="user-group">
            <button type="button" className="user-group-header" onClick={() => toggleGroup(group.key)}>
              <div className="user-group-left">
                <span className="user-group-dot" style={{ backgroundColor: group.color }} />
                <strong>{group.key}</strong>
              </div>
              <div className="user-group-right">
                <span>{group.users.length} kişi</span>
                <span>{expandedGroupMap[group.key] ? '▲' : '▼'}</span>
              </div>
            </button>

            {expandedGroupMap[group.key] && (
              <div className="users-grid">
                {group.users.map((user) => {
                  const roleInfo = getRoleBadge(user.role_id, user.job_title);

                  return (
                    <div key={user.id} className="user-card">
                      <div className="user-header">
                        <div className="user-card-info">
                          <h3>{user.username}</h3>
                          <p className="user-card-email">{user.email}</p>
                        </div>
                        <div className="role-badge" style={{ backgroundColor: roleInfo.color }}>
                          {roleInfo.label}
                        </div>
                      </div>

                      <div className="user-details">
                        <div className="detail-item">
                          <span className="label">Kayıt Tarihi:</span>
                          <span className="value">{new Date(user.created_at).toLocaleDateString('tr-TR')}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Görev:</span>
                          <span className="value">{user.job_title || '-'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Telefon:</span>
                          <span className="value">{user.phone_number || '-'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Şifre Durumu:</span>
                          <span className="value">{user.must_change_password ? 'İlk girişte değişim zorunlu' : 'Normal'}</span>
                        </div>
                        <div className="detail-item">
                          <span className="label">Durum:</span>
                          <span className="value">{user.is_active === false ? 'Pasif' : 'Aktif'}</span>
                        </div>
                      </div>

                      <div className="user-actions">
                        <button
                          className="reminder-btn"
                          onClick={() => handleSendReminder(user)}
                          title="Hatırlatma Gönder"
                        >
                          Hatırlatma Gönder
                        </button>
                        {canEditUser(user) && (
                          <button className="edit-btn" onClick={() => handleResetPassword(user)} title="Şifre Yenile">
                            Şifre Yenile
                          </button>
                        )}
                        {canEditUser(user) && (
                          <button className="edit-btn" onClick={() => startEditingUser(user)} title="Düzenle">
                            Düzenle
                          </button>
                        )}
                        {canDeleteUser(user) && (
                          <button className="delete-btn" onClick={() => handleDeleteUser(user.id)} title="Sil">
                            Sil
                          </button>
                        )}
                      </div>

                      {editingUserId === user.id && (
                        <div className="edit-user-panel">
                          <div className="form-row">
                            <div className="form-group">
                              <label>Kullanıcı Adı</label>
                              <input
                                type="text"
                                name="username"
                                value={editFormData.username}
                                onChange={handleEditInputChange}
                              />
                            </div>
                            <div className="form-group">
                              <label>E-posta</label>
                              <input
                                type="email"
                                name="email"
                                value={editFormData.email}
                                onChange={handleEditInputChange}
                              />
                            </div>
                            <div className="form-group">
                              <label>Telefon</label>
                              <input
                                type="text"
                                name="phone_number"
                                value={editFormData.phone_number}
                                onChange={handleEditInputChange}
                              />
                            </div>
                          </div>

                          <div className="form-row">
                            <div className="form-group">
                              <label>Rol</label>
                              <select
                                name="role_id"
                                value={editFormData.role_id}
                                onChange={handleEditInputChange}
                                disabled={
                                  Number(user.role_id) === ROLE_IDS.ADMIN &&
                                  !isSuperAdmin &&
                                  Number(user.id) !== Number(currentUser?.id)
                                }
                              >
                                {roles.map((role) => (
                                  <option key={role.id} value={role.id}>
                                    {ROLE_META[role.name]?.label || role.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="form-group">
                              <label>Görev / Ünvan</label>
                              <input
                                type="text"
                                name="custom_job_title"
                                value={editFormData.custom_job_title}
                                onChange={handleEditInputChange}
                              />
                            </div>
                            <div className="form-group">
                              <label>Durum</label>
                              <select
                                name="is_active"
                                value={editFormData.is_active ? 'true' : 'false'}
                                onChange={(event) =>
                                  setEditFormData((prev) => ({
                                    ...prev,
                                    is_active: event.target.value === 'true',
                                  }))
                                }
                                disabled={!isCurrentAdmin}
                              >
                                <option value="true">Aktif</option>
                                <option value="false">Pasif</option>
                              </select>
                            </div>
                          </div>

                          <div className="edit-actions">
                            <button className="save-btn" onClick={() => handleUpdateUser(user)} type="button">
                              Kaydet
                            </button>
                            <button className="cancel-btn" onClick={cancelEditingUser} type="button">
                              İptal
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        ))
      )}
    </div>
  );
};

export default Users;
